import React, { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider, useApp } from './context/AppContext';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { NewVisitPage } from './pages/NewVisitPage';
import { VisitsPage } from './pages/VisitsPage';
import { CollectionsPage } from './pages/CollectionsPage';
import { SalesPage } from './pages/SalesPage';
import { ReportsPage } from './pages/ReportsPage';
import { UsersPage } from './pages/UsersPage';
import { RolesPage } from './pages/RolesPage';
import { MapPage } from './pages/MapPage';
import { ClientsPage } from './pages/ClientsPage';
import { SettingsPage } from './pages/SettingsPage';
import { BitacoraPage } from './pages/BitacoraPage';
import { CuentasCobrarPage } from './pages/CuentasCobrarPage';
import { PedidosPage } from './pages/PedidosPage';
import { InventarioPage } from './pages/InventarioPage';
import { CajaVaciaPage } from './pages/CajaVaciaPage';
import { PlanificacionPage } from './pages/PlanificacionPage';
import { FacturasDejadasPage } from './pages/FacturasDejadasPage';
import { DevolucionesPage } from './pages/DevolucionesPage';
import { OfertasPage } from './pages/OfertasPage';
import { ComisionesPage } from './pages/ComisionesPage';
import { Permissions } from './types';
type Page = 'dashboard' | 'visits' | 'new-visit' | 'collections' | 'sales' | 'reports' | 'users' | 'roles' | 'map' | 'clients' | 'settings' | 'bitacora' | 'cuentas-cobrar' | 'pedidos' | 'inventario' | 'caja-vacia' | 'planificacion' | 'facturas-dejadas' | 'devoluciones' | 'ofertas' | 'comisiones';
const pageConfig: Record<Page, {
  title: string;
  subtitle: string;
  permission: keyof Permissions | 'settings';
}> = {
  dashboard: {
    title: 'Panel Principal',
    subtitle: 'Resumen de actividad',
    permission: 'dashboard'
  },
  visits: {
    title: 'Mis Visitas',
    subtitle: 'Historial de visitas realizadas',
    permission: 'visits'
  },
  'new-visit': {
    title: 'Nueva Visita',
    subtitle: 'Registrar visita a cliente',
    permission: 'new-visit'
  },
  collections: {
    title: 'Cobros',
    subtitle: 'Gestión de cobros y pagos',
    permission: 'collections'
  },
  sales: {
    title: 'Ventas',
    subtitle: 'Registro de ventas realizadas',
    permission: 'sales'
  },
  clients: {
    title: 'Farmacias',
    subtitle: 'Gestión de farmacias y ubicaciones',
    permission: 'clients'
  },
  reports: {
    title: 'Reportes',
    subtitle: 'Informes y estadísticas',
    permission: 'reports'
  },
  users: {
    title: 'Usuarios',
    subtitle: 'Administración de usuarios',
    permission: 'users'
  },
  roles: {
    title: 'Roles y Permisos',
    subtitle: 'Configuración de acceso',
    permission: 'roles'
  },
  map: {
    title: 'Mapa GPS',
    subtitle: 'Ubicación de visitas',
    permission: 'map'
  },
  settings: {
    title: 'Configuración',
    subtitle: 'Perfil, seguridad y notificaciones',
    permission: 'settings'
  },
  bitacora: {
    title: 'Bitácora',
    subtitle: 'Registro de actividad del sistema',
    permission: 'bitacora'
  },
  'cuentas-cobrar': {
    title: 'Cuentas por Cobrar',
    subtitle: 'Gestión de facturas pendientes',
    permission: 'cuentas_cobrar'
  },
  pedidos: {
    title: 'Pedidos',
    subtitle: 'Gestión de órdenes de compra',
    permission: 'pedidos'
  },
  inventario: {
    title: 'Inventario',
    subtitle: 'Control de stock de productos',
    permission: 'inventario'
  },
  'caja-vacia': {
    title: 'Caja Vacía',
    subtitle: 'Compra de cajas vacías',
    permission: 'caja_vacia'
  },
  'facturas-dejadas': {
    title: 'Facturas Dejadas',
    subtitle: 'Control de facturas físicas entregadas',
    permission: 'facturas_dejadas'
  },
  devoluciones: {
    title: 'Devoluciones',
    subtitle: 'Registro de productos devueltos o vencidos',
    permission: 'devoluciones'
  },
  ofertas: {
    title: 'Ofertas e Impulsos',
    subtitle: 'Gestión de productos destacados',
    permission: 'ofertas'
  },
  comisiones: {
    title: 'Comisiones',
    subtitle: 'Gestión de comisiones por venta y cobro',
    permission: 'comisiones'
  },
  planificacion: {
    title: 'Planificación',
    subtitle: 'Gestión comercial semanal',
    permission: 'visits'
  }
};
function AppContent() {
  const {
    isAuthenticated,
    isLoading,
    user
  } = useAuth();
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const hasPermission = (page: Page) => {
    if (!user) return false;
    if (page === 'settings') return true;
    if (page === 'clients') {
      return user.rolNombre === 'Administrador' || user.permisos?.users === true || user.permisos?.clients === true;
    }
    if (page === 'bitacora') {
      return user.rolNombre === 'Administrador' || user.permisos?.bitacora === true;
    }
    // Default to true for new modules if permission key doesn't exist yet (for backward compatibility)
    if (['cuentas-cobrar', 'pedidos', 'inventario', 'caja-vacia', 'facturas-dejadas', 'devoluciones', 'ofertas', 'comisiones', 'sales'].includes(page)) {
      const permKey = pageConfig[page].permission as keyof Permissions;
      return user.rolNombre === 'Administrador' || user.permisos?.[permKey] === true || user.permisos?.[permKey] === undefined;
    }
    return user.permisos[pageConfig[page].permission as keyof Permissions] === true;
  };
  useEffect(() => {
    if (user && !hasPermission(currentPage)) {
      const firstPermitted = Object.keys(pageConfig).find(key => hasPermission(key as Page)) as Page;
      if (firstPermitted) setCurrentPage(firstPermitted);
    }
  }, [user, currentPage]);
  if (isLoading) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-id="element-0">
        <div className="text-center" data-id="element-1">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-2" />
          <p className="text-slate-400 text-sm" data-id="element-3">Cargando...</p>
        </div>
      </div>;
  }
  if (!isAuthenticated) return <LoginPage data-id="element-4" />;
  const renderPage = () => {
    if (user && !hasPermission(currentPage)) {
      return <div className="flex flex-col items-center justify-center h-[60vh] text-center" data-id="element-5">
          <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mb-4" data-id="element-6">
            <svg className="w-8 h-8 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-7">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" data-id="element-8" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900" data-id="element-9">Acceso Denegado</h2>
          <p className="text-slate-500 mt-2" data-id="element-10">
            No tienes permisos para ver esta página.
          </p>
        </div>;
    }
    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage data-id="element-11" />;
      case 'visits':
        return <VisitsPage data-id="element-12" />;
      case 'new-visit':
        return <NewVisitPage data-id="element-13" />;
      case 'collections':
        return <CollectionsPage data-id="element-14" />;
      case 'sales':
        return <SalesPage data-id="element-15" />;
      case 'clients':
        return <ClientsPage data-id="element-16" />;
      case 'reports':
        return <ReportsPage data-id="element-17" />;
      case 'users':
        return <UsersPage data-id="element-18" />;
      case 'roles':
        return <RolesPage data-id="element-19" />;
      case 'map':
        return <MapPage data-id="element-20" />;
      case 'settings':
        return <SettingsPage data-id="element-21" />;
      case 'bitacora':
        return <BitacoraPage data-id="element-22" />;
      case 'cuentas-cobrar':
        return <CuentasCobrarPage data-id="element-23" />;
      case 'pedidos':
        return <PedidosPage data-id="element-24" />;
      case 'inventario':
        return <InventarioPage data-id="element-25" />;
      case 'caja-vacia':
        return <CajaVaciaPage data-id="element-26" />;
      case 'facturas-dejadas':
        return <FacturasDejadasPage data-id="element-27" />;
      case 'devoluciones':
        return <DevolucionesPage data-id="element-28" />;
      case 'ofertas':
        return <OfertasPage data-id="element-29" />;
      case 'comisiones':
        return <ComisionesPage data-id="element-30" />;
      case 'planificacion':
        return <PlanificacionPage data-id="element-31" />;
      default:
        return <DashboardPage data-id="element-32" />;
    }
  };
  const config = pageConfig[currentPage];
  return <Layout currentPage={currentPage} onNavigate={page => setCurrentPage(page as Page)} title={config.title} subtitle={config.subtitle} data-id="element-33">
      {renderPage()}
    </Layout>;
}
function NewOrderBanner() {
  const {
    isAuthenticated
  } = useAuth();
  const {
    newOrderAlert,
    clearNewOrderAlert
  } = useApp();
  if (!isAuthenticated || !newOrderAlert) return null;
  return <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between gap-4 px-6 py-4 bg-orange-500 text-white shadow-2xl animate-bounce" data-id="element-34">
      <div className="flex items-center gap-3 font-bold text-lg" data-id="element-35">
        <span className="text-2xl" data-id="element-36">🛒</span>
        ¡NUEVO PEDIDO RECIBIDO! — Revisa el módulo de Pedidos
      </div>
      <button onClick={clearNewOrderAlert} className="px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-semibold transition-colors" data-id="element-37">
        Cerrar
      </button>
    </div>;
}
export function App() {
  return <AuthProvider data-id="element-38">
      <AppProvider data-id="element-39">
        <NewOrderBanner data-id="element-40" />
        <AppContent data-id="element-41" />
      </AppProvider>
    </AuthProvider>;
}